import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { CmApiHttpError, cmApi } from '@/api/cmApi'
import { authHeaders, readAuth } from '@/auth/session'
import {
  asBlockedDeviceResponse,
  asGetUserLogResponse,
  toLoginHistoryResult,
  type LoginHistoryResult,
} from '@/api/map/userLog'

export type LoginHistoryQuery = {
  memberId: string
  fromDate: string
  toDate: string
  page?: number
  pageSize?: number
  /** Bumped on every Search so same dates still hit the network. */
  searchNonce?: number
}

export type {
  LoginHistoryRow,
  LoginHistoryResult,
} from '@/api/map/userLog'

function emptyResult(query: LoginHistoryQuery): LoginHistoryResult {
  const pageSize = Math.max(1, query.pageSize ?? DEFAULT_PAGE_SIZE)
  const page = Math.max(1, query.page ?? 1)
  return { rows: [], total: 0, page, pageSize }
}

/** Exported for Vitest whitebox (soft-empty / envelope mapping). */
export async function loadLoginHistory(query: LoginHistoryQuery): Promise<LoginHistoryResult> {
  try {
    const live = await cmApi.getUserLog(authHeaders(), {
      memberId: query.memberId,
      fromDate: query.fromDate,
      toDate: query.toDate,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
    })
    const envelope = asGetUserLogResponse(live)
    if (!envelope?.value) return emptyResult(query)
    return toLoginHistoryResult(envelope, query)
  } catch (err) {
    // Soft-empty only when the CM proxy route is missing / down — other faults
    // must surface so Search/retry actually re-hits the network.
    if (err instanceof CmApiHttpError && (err.status === 404 || err.status === 503)) {
      console.warn('[login-history] CM userLog proxy unavailable', err)
      return emptyResult(query)
    }
    throw err
  }
}

/** Member Detail → Login History — CM-API → hierarchy getuserLog. */
export function useLoginHistory(query: LoginHistoryQuery, enabled = true) {
  const auth = readAuth()
  const { searchNonce: _searchNonce, ...request } = query
  return useQuery({
    queryKey: ['cm', 'login-history', query],
    queryFn: () => loadLoginHistory(request),
    enabled: enabled && Boolean(auth) && Boolean(query.memberId),
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: (previous) => previous,
  })
}

export function useSaveMemberBlockedDeviceId() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { memberId: string; comment?: string }) => {
      const live = await cmApi.saveBlockedDeviceId(authHeaders(), {
        memberId: input.memberId,
        comment: input.comment?.trim() ?? '',
      })
      const res = asBlockedDeviceResponse(live)
      if (!res.value) {
        throw new CmApiHttpError(409, res.error || 'Unable to block device id')
      }
      return res
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['cm', 'login-history'] })
      void client.invalidateQueries({ queryKey: ['cm', 'blocked-devices'] })
    },
  })
}

export function useDeleteMemberBlockedDeviceId() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { memberId: string }) => {
      const live = await cmApi.deleteBlockedDeviceId(authHeaders(), {
        memberId: input.memberId,
      })
      const res = asBlockedDeviceResponse(live)
      if (!res.value) {
        throw new CmApiHttpError(409, res.error || 'Unable to remove blocked device id')
      }
      return res
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['cm', 'login-history'] })
      void client.invalidateQueries({ queryKey: ['cm', 'blocked-devices'] })
    },
  })
}
